import type { JSX } from 'react';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { jiraKeys, useJiraIssues, useJiraStatus } from '~/hooks/useJira';
import { useUIStore } from '~/stores/uiStore';

import { isJiraReconnectRequired } from './jiraErrors';
import { JiraIssueDetail } from './JiraIssueDetail';
import { JiraSidebar } from './JiraSidebar';

/**
 * JiraLayout - Master-detail layout for Jira integration.
 *
 * Structure:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                     Header (top nav)                        │
 * ├───────────────┬─────────────────────────────────────────────┤
 * │  JiraSidebar  │           JiraIssueDetail                   │
 * │  (left panel) │                                             │
 * │  280px fixed  │           flex-1 (fills remaining space)    │
 * └───────────────┴─────────────────────────────────────────────┘
 *
 * Note: Header and Footer are provided by ProtectedLayout parent.
 */
export function JiraLayout(): JSX.Element {
  const { status } = useJiraStatus();
  const { data: issuesData, error, isLoading, refetch } = useJiraIssues();
  const { closeJiraTicketModal, openJiraTicketModal, selectedJiraTicket } = useUIStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Invalidate status when reconnect needed (so header button updates)
  useEffect(() => {
    if (isJiraReconnectRequired(error)) {
      void queryClient.invalidateQueries({ queryKey: jiraKeys.status() });
    }
  }, [error, queryClient]);

  // Clear selected ticket when unmounting
  useEffect(() => {
    return (): void => {
      closeJiraTicketModal();
    };
  }, [closeJiraTicketModal]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleReconnect = useCallback(() => {
    void navigate('/profile');
  }, [navigate]);

  const handleSelectIssue = useCallback(
    (issueKey: string) => {
      const issue = issuesData?.items.find(i => i.key === issueKey);
      if (issue) {
        openJiraTicketModal(issue);
      }
    },
    [issuesData?.items, openJiraTicketModal],
  );

  const isConnected = status?.connected;
  const hasReconnectError = isJiraReconnectRequired(error);

  return (
    <div className="flex h-full min-h-0">
      {/* Left sidebar - issue list */}
      <JiraSidebar
        error={error}
        hasReconnectError={hasReconnectError}
        isConnected={isConnected ?? false}
        isLoading={isLoading}
        issues={issuesData?.items ?? []}
        onReconnect={handleReconnect}
        onRetry={handleRetry}
        onSelectIssue={handleSelectIssue}
        selectedIssueKey={selectedJiraTicket?.key ?? null}
        siteUrl={status?.site_url ?? null}
      />

      {/* Main content area - issue detail */}
      <main className="flex-1 overflow-y-auto bg-background">
        <JiraIssueDetail issue={selectedJiraTicket} />
      </main>
    </div>
  );
}
